import { InMemoryWebStorage, WebStorageStateStore } from 'oidc-client-ts';
import { isE2BEnvironment, getBackendUrl } from '@/utils/environment';

/**
 * OIDC Configuration for Access Identity Integration
 * 
 * This configuration supports BOTH authentication modes:
 * - Iframe mode: Silent authentication via silent_redirect_uri
 * - Standalone mode: Interactive authentication via redirect_uri
 * 
 * For e2b.app environments, uses backend proxy to bypass CORS restrictions.
 */
export function getOidcConfig() {
  const isE2B = isE2BEnvironment();
  const backendUrl = getBackendUrl();
  
  return {
    // Client ID from Access Identity
    client_id: import.meta.env.VITE_OIDC_CLIENT_ID,
    
    // Redirect URIs for both authentication modes
    redirect_uri: `${window.location.origin}/login/callback`,        // Interactive flow (standalone)
    silent_redirect_uri: `${window.location.origin}/login/callback`, // Silent flow (iframe)
    
    // Requested scopes
    scope: 'openid profile email policy workflow.automation.api access.widget.api access.cloud.financials.framework',
    
    // Use in-memory storage to avoid conflicts with multiple tabs/windows
    // This ensures tokens are not shared between different instances
    userStore: new WebStorageStateStore({ 
      store: new InMemoryWebStorage() 
    }),
    
    // Automatically renew tokens before they expire
    automaticSilentRenew: true,

    // Try to renew tokens 5 minutes before expiry (gives more buffer for renewal)
    accessTokenExpiringNotificationTimeSeconds: 300,

    // Load user info from the /userinfo endpoint
    loadUserInfo: true,
    
    // Response type for authorization code flow with PKCE
    response_type: 'code',

    // DEV ONLY: Force org selection at login (when running locally)
    // This allows developers to choose which Evo instance to authenticate with
    ...(import.meta.env.DEV && {
      extraQueryParams: {
        prompt: 'select_account', // Force account/org selection
      },
    }),
    
    // Don't include state in the URL for cleaner URLs
    // The state is still maintained securely in memory
    includeIdTokenInSilentRenew: true,
    
    // CORS BYPASS FOR E2B.APP: Use backend proxy endpoint
    // This avoids CORS issues with dynamic e2b.app instance IDs
    // The proxy endpoint fetches OIDC config server-side (no CORS restrictions)
    ...(isE2B && {
      metadataUrl: `${backendUrl}/api/identity/config`,
      metadata: undefined, // Force fetch from metadataUrl instead of .well-known
    }),
  };
}

/**
 * Product Settings Type (from @workspace/utils-react)
 */
export interface ProductSettings {
  identityUrl: string;
  workspaceUrl: string;
  apiUrl?: string;
  [key: string]: any;
}
